/**
 * Hook useProgressiveLoad - Chargement progressif des composants
 * 
 * Utilisation simplifiée du système de performance pour le chargement
 * adaptatif basé sur la qualité de connexion.
 */

import { useState, useEffect, useCallback } from 'react';
import { NetworkQualityService, ConnectionQuality } from '../services/performance/NetworkQualityService';

export type LoadPriority = 'critical' | 'high' | 'medium' | 'low' | 'lazy';

interface UseProgressiveLoadOptions {
  /** Priorité de chargement */
  priority?: LoadPriority;
  /** Délai minimum avant chargement (ms) */
  minDelay?: number;
  /** Délai maximum avant chargement (ms) */
  maxDelay?: number;
  /** Charger immédiatement sans délai */
  immediate?: boolean;
}

interface UseProgressiveLoadReturn {
  /** Le composant est prêt à être affiché */
  isReady: boolean;
  /** Le composant est en cours de chargement */
  isLoading: boolean;
  /** Afficher le skeleton */
  showSkeleton: boolean;
  /** Activer les animations (basé sur la connexion) */
  enableAnimations: boolean;
  /** Mode réduit (connexion lente) */
  reducedMode: boolean;
  /** Qualité de connexion actuelle */
  connectionQuality: ConnectionQuality;
  /** Forcer le chargement */
  forceLoad: () => void;
}

/**
 * Délais de chargement par priorité et qualité de connexion
 */
const PRIORITY_DELAYS: Record<LoadPriority, Record<ConnectionQuality, number>> = {
  critical: { excellent: 0, good: 0, moderate: 0, slow: 0, offline: 0 },
  high: { excellent: 0, good: 50, moderate: 100, slow: 200, offline: 300 },
  medium: { excellent: 50, good: 150, moderate: 300, slow: 500, offline: 800 },
  low: { excellent: 150, good: 300, moderate: 600, slow: 1000, offline: 1500 },
  lazy: { excellent: 300, good: 600, moderate: 1200, slow: 2000, offline: 3000 },
};

/**
 * Hook pour le chargement progressif d'un composant
 */
export function useProgressiveLoad(
  options: UseProgressiveLoadOptions = {}
): UseProgressiveLoadReturn {
  const {
    priority = 'medium',
    minDelay,
    maxDelay,
    immediate = false,
  } = options;

  const [isReady, setIsReady] = useState(immediate || priority === 'critical');
  const [isLoading, setIsLoading] = useState(!immediate && priority !== 'critical');
  const [connectionQuality, setConnectionQuality] = useState<ConnectionQuality>(
    NetworkQualityService.getQuality()
  );

  // Écouter les changements de qualité réseau
  useEffect(() => {
    const unsubscribe = NetworkQualityService.subscribe((info) => {
      setConnectionQuality(info.quality);
    });
    return unsubscribe;
  }, []);

  // Calculer le délai basé sur la priorité et la qualité de connexion
  const getDelay = useCallback(() => {
    if (immediate) return 0;
    
    let delay = PRIORITY_DELAYS[priority][connectionQuality];
    
    if (minDelay !== undefined) {
      delay = Math.max(delay, minDelay);
    }
    if (maxDelay !== undefined) {
      delay = Math.min(delay, maxDelay);
    }
    
    return delay;
  }, [priority, connectionQuality, minDelay, maxDelay, immediate]);

  // Gérer le chargement
  useEffect(() => {
    if (isReady) return;

    const delay = getDelay();
    
    const timer = setTimeout(() => {
      setIsLoading(false);
      setIsReady(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [getDelay, isReady]);

  // Forcer le chargement
  const forceLoad = useCallback(() => {
    setIsLoading(false);
    setIsReady(true);
  }, []);

  // Recommandations basées sur la qualité
  const recommendations = NetworkQualityService.getLoadingRecommendations();

  return {
    isReady,
    isLoading,
    showSkeleton: !isReady,
    enableAnimations: recommendations.enableAnimations,
    reducedMode: recommendations.reducedMode,
    connectionQuality,
    forceLoad,
  };
}

/**
 * Hook pour le chargement de données avec priorité
 */
export function useProgressiveData<T>(
  fetcher: () => Promise<T>,
  options: UseProgressiveLoadOptions & {
    /** Dépendances pour refetch */
    deps?: React.DependencyList;
    /** Valeur initiale */
    initialValue?: T;
    /** Activer le chargement automatique */
    enabled?: boolean;
  } = {}
) {
  const {
    priority = 'medium',
    deps = [],
    initialValue,
    enabled = true,
    ...loadOptions
  } = options;

  const [data, setData] = useState<T | undefined>(initialValue);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const { isReady, connectionQuality, reducedMode, enableAnimations } = useProgressiveLoad({
    priority,
    ...loadOptions,
  });

  // Charger les données quand le composant est prêt
  useEffect(() => {
    if (!isReady || !enabled) return;

    let cancelled = false;

    const loadData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await fetcher();
        if (!cancelled) {
          setData(result);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Unknown error'));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [isReady, enabled, ...deps]);

  // Fonction de refetch
  const refetch = useCallback(async () => {
    if (!enabled) return;
    
    setIsLoading(true);
    setError(null);

    try {
      const result = await fetcher();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      setIsLoading(false);
    }
  }, [fetcher, enabled]);

  return {
    data,
    error,
    isLoading,
    isReady: isReady && !isLoading && data !== undefined,
    showSkeleton: !isReady || isLoading,
    connectionQuality,
    reducedMode,
    enableAnimations,
    refetch,
  };
}

/**
 * Hook pour détecter si un élément est visible dans le viewport
 */
export function useViewportVisibility(
  options: {
    rootMargin?: string;
    threshold?: number;
    triggerOnce?: boolean;
  } = {}
) {
  const { rootMargin = '100px', threshold = 0.1, triggerOnce = true } = options;
  
  const [ref, setRef] = useState<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!ref) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.disconnect();
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { rootMargin, threshold }
    );

    observer.observe(ref);
    return () => observer.disconnect();
  }, [ref, rootMargin, threshold, triggerOnce]);

  return { ref: setRef, isVisible };
}

/**
 * Hook simplifié pour la qualité réseau
 */
export function useNetworkStatus() {
  const [status, setStatus] = useState(() => ({
    quality: NetworkQualityService.getQuality(),
    isOnline: NetworkQualityService.isOnline(),
    ...NetworkQualityService.getLoadingRecommendations(),
  }));

  useEffect(() => {
    const unsubscribe = NetworkQualityService.subscribe((info) => {
      setStatus({
        quality: info.quality,
        isOnline: info.isOnline,
        ...NetworkQualityService.getLoadingRecommendations(),
      });
    });
    return unsubscribe;
  }, []);

  return status;
}
